/**
 * Vyro chat embed — browser entry point.
 *
 * Built to a standalone minified IIFE (`dist/chat.js`) and served from the Vyro
 * embed CDN. A dealer's site (or their marketing agency, via Google Tag
 * Manager) loads it with a single tag:
 *
 *   <script async src="https://embed.vyro.com.au/chat.js"
 *     data-vyro-showroom="giltrap"
 *     data-vyro-host="https://<embeds-host>"
 *     id="vyro-chat-embed"></script>
 *
 * It self-places a chat launcher in an isolated iframe — no page edits, no
 * cooperation from the host's website platform required.
 */
import { findScript, resolveConfig } from './config'
import { initChatEmbed } from './loader'

interface AutorunWindow extends Window {
  __VYRO_CHAT_EMBED_NO_AUTORUN?: boolean
}

// Captured at module-eval time so `document.currentScript` is still meaningful
// even if `boot` later runs from a DOMContentLoaded callback.
const currentScript =
  typeof document !== 'undefined' ? document.currentScript : null

export function boot(): void {
  try {
    const config = resolveConfig(findScript(document, currentScript))
    if (!config) return
    initChatEmbed(window, config)
  } catch {
    // Never let an embed failure surface on a dealer's site.
  }
}

if (
  typeof window !== 'undefined' &&
  !(window as AutorunWindow).__VYRO_CHAT_EMBED_NO_AUTORUN
) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true })
  } else {
    boot()
  }
}
